<template>
    <div class="locale-font">
        <div class="breadcrumb-container">
            <v-breadcrumbs :items="breadcrumbsItem"></v-breadcrumbs>
        </div>
        <div class="header">
            <div class="locale-font">
                {{ bureau }}
            </div>
            <div class="action-header">
                <v-btn
                    @click="cancel()"
                    outlined
                    class="mr-2"
                    width="180"
                    color="primary">
                    {{ $vuetify.lang.t('$vuetify.workdayForm.cancel') }}
                </v-btn>

                <v-btn
                    v-if="canSaveOrUpdate"
                    @click="save()"
                    width="180"
                    color="primary">
                    {{ $vuetify.lang.t('$vuetify.workdayForm.save') }}
                </v-btn>
            </div>
        </div>
        <v-form
            lazy-validation
            ref="form"
            v-model="valid">
            <v-card
                class="card-container-header"
            >
                <div class="row">
                    <div class="col-8">
                        <span class="locale-font label-text">
                            {{ $vuetify.lang.t('$vuetify.workdayForm.bureau') }}
                        </span>
                        <v-text-field
                            v-model="bureau"
                            autocomplete="off"
                            class="name-input"
                            filled
                            dense
                            readonly
                            :rules="inputRules"
                            :error-messages="errors.name">
                        </v-text-field>
                    </div>
                    <div class="col-4 mb-6 text-box">
                        <i class="fas fa-lightbulb-on"> {{$vuetify.lang.t('$vuetify.workdayForm.information')}}</i>
                        <span>
                            <ul>
                                <li class="icon-text">{{$vuetify.lang.t('$vuetify.workdayForm.workingTimeEachDay')}}</li>
                                <li class="icon-text">{{$vuetify.lang.t('$vuetify.workdayForm.limitsWorkingHours')}}</li>
                                <li class="icon-text">{{$vuetify.lang.t('$vuetify.workdayForm.maximumLimitsOvertime')}}</li>
                            </ul>
                        </span>
                    </div>
                </div>
                <div class="column-head locale-font label-text">
                    <div>
                        <div
                            class="column-item"
                            v-for="item in header"
                            :key="item">
                            {{ item }}
                        </div>
                    </div>
                </div>
            </v-card>
            <v-card
                v-if="showWorkday"
                class="card-container-content">
                <div class="column-head"
                     v-for="(item, index) in form.workdayData"
                     :key="item.day">
                    <div>
                        <div class="column-item">
                            {{ days[item.dayIndex] }}
                        </div>
                        <div class="column-item">
                            <v-text-field
                                v-model="item.checkinTime"
                                autocomplete="off"
                                type="time"
                                step="0"
                                :error-messages="errors.checkInTime[index]"
                                :disabled="item.dayIndex === todayIndex"
                                :readonly="!item.isWorkday && item.dayIndex !== todayIndex">
                            </v-text-field>
                        </div>
                        <div class="column-item">
                            <v-text-field
                                v-model="item.startTime"
                                autocomplete="off"
                                type="time"
                                step="0"
                                :error-messages="errors.startTime[index]"
                                :disabled="item.dayIndex === todayIndex"
                                :readonly="!item.isWorkday && item.dayIndex !== todayIndex">
                            </v-text-field>
                        </div>
                        <div class="column-item">
                            <v-text-field
                                v-model="item.lateCheckinTime"
                                autocomplete="off"
                                type="time"
                                step="0"
                                :error-messages="errors.lateCheckInTime[index]"
                                :disabled="item.dayIndex === todayIndex"
                                :readonly="!item.isWorkday && item.dayIndex !== todayIndex">
                            </v-text-field>
                        </div>
                        <div class="column-item">
                            <v-text-field
                                v-model="item.lastCheckinTime"
                                autocomplete="off"
                                type="time"
                                step="0"
                                :error-messages="errors.lastCheckInTime[index]"
                                :disabled="item.dayIndex === todayIndex"
                                :readonly="!item.isWorkday && item.dayIndex !== todayIndex">
                            </v-text-field>
                        </div>
                        <div class="column-item">
                            <v-text-field
                                v-model="item.breakHours"
                                autocomplete="off"
                                append-icon="mdi-timer"
                                v-mask="'##:##'"
                                :disabled="item.dayIndex === todayIndex"
                                :error-messages="errors.breakhours[index]"
                                :readonly="!item.isWorkday && item.dayIndex !== todayIndex">
                            </v-text-field>
                        </div>
                        <div class="column-item">
                            <v-text-field
                                v-model="item.endTime"
                                autocomplete="off"
                                type="time"
                                step="0"
                                :disabled="item.dayIndex === todayIndex"
                                :error-messages="errors.endTime[index]"
                                :readonly="!item.isWorkday && item.dayIndex !== todayIndex">
                            </v-text-field>
                        </div>
                        <div class="column-item">
                            <v-text-field
                                v-model="item.totalWorkHours"
                                autocomplete="off"
                                append-icon="mdi-timer"
                                v-mask="'##:##'"
                                :disabled="item.dayIndex === todayIndex"
                                :error-messages="errors.totalWorkTime[index]"
                                readonly>
                            </v-text-field>
                        </div>
                        <div
                            class="column-item"
                            :style="{ 'pointer-events': item.dayIndex === todayIndex ? 'none' : null }">
                            <toggle-status
                                v-model="item.isWorkday"
                                :disabled="item.dayIndex === todayIndex"
                            />
                        </div>
                    </div>
                    <div class="row-item" v-if="item.dayIndex === todayIndex">
                        <v-icon
                            color="#6c98ba"
                            size="14">
                            information
                        </v-icon>
                        <span>{{ $vuetify.lang.t('$vuetify.workdayForm.msgCannotModify') }}</span>
                    </div>
                </div>
            </v-card>
        </v-form>
    </div>
</template>

<script>
import ToggleStatus from '@/components/ToggleStatus.vue'
import { mask } from 'vue-the-mask'

export default {
    name: 'WorkdayForm',

    components: {
        ToggleStatus
    },

    directives: {
        mask
    },
    computed: {
        breadcrumbsItem () {
            return [
                {
                    text: this.$vuetify.lang.t('$vuetify.workday.title'),
                    disabled: false,
                    exact: true,
                    to: {
                        name: 'TimeTableSetting'
                    }
                },
                {
                    text: this.$vuetify.lang.t('$vuetify.workdayForm.title'),
                    disabled: true
                }
            ]
        },
        header () {
            return [
                this.$vuetify.lang.t('$vuetify.workdayForm.day'),
                this.$vuetify.lang.t('$vuetify.workdayForm.checkInTime'),
                this.$vuetify.lang.t('$vuetify.workdayForm.startTime'),
                this.$vuetify.lang.t('$vuetify.workdayForm.lateCheckInTime'),
                this.$vuetify.lang.t('$vuetify.workdayForm.lastCheckInTime'),
                this.$vuetify.lang.t('$vuetify.workdayForm.breakHours'),
                this.$vuetify.lang.t('$vuetify.workdayForm.endTime'),
                this.$vuetify.lang.t('$vuetify.workdayForm.totalWorkHours'),
                this.$vuetify.lang.t('$vuetify.workdayForm.status')
            ]
        },
        days () {
            return [
                this.$vuetify.lang.t('$vuetify.workdayForm.days.monday'),
                this.$vuetify.lang.t('$vuetify.workdayForm.days.tuesday'),
                this.$vuetify.lang.t('$vuetify.workdayForm.days.wednesday'),
                this.$vuetify.lang.t('$vuetify.workdayForm.days.thursday'),
                this.$vuetify.lang.t('$vuetify.workdayForm.days.friday'),
                this.$vuetify.lang.t('$vuetify.workdayForm.days.saturday'),
                this.$vuetify.lang.t('$vuetify.workdayForm.days.sunday')
            ]
        }
    },
    data () {
        return {
            bureau: null,
            todayIndex: null,
            form: {
                isCheckInByLine: true,
                workdayData: []
            },
            errors: {
                name: null,
                checkInTime: [],
                startTime: [],
                lateCheckInTime: [],
                lastCheckInTime: [],
                breakhours: [],
                endTime: [],
                totalWorkTime: [],
                dayIndex: []
            },
            valid: true,
            inputRules: [
                v => this.$validator.hasValue(v, this.$vuetify.lang.t('$vuetify.workdayForm.msgRequiredField')),
                v => this.$validator.lengthNotExceed(v, 255, this.$vuetify.lang.t('$vuetify.workdayForm.msgExceededField'))
            ],
            canSaveOrUpdate: false,
            showWorkday: false
        }
    },

    methods: {
        isCheckInTimeOverlapStartTime (index) {
            let isOverlap = false
            if (this.form.workdayData[index].isWorkday) {
                if (this.form.workdayData[index].checkinTime > this.form.workdayData[index].startTime) {
                    isOverlap = true
                }
            }

            this.errors.checkInTime[index] = isOverlap ? `${this.$vuetify.lang.t('$vuetify.workdayForm.checkInTimeOverlapStartTime')} ${this.form.workdayData[index].checkinTime}.` : null
            this.errors.startTime[index] = isOverlap ? `${this.$vuetify.lang.t('$vuetify.workdayForm.startTimeOverlapCheckInTime')} ${this.form.workdayData[index].startTime}.` : null

            return isOverlap
        },

        isCheckInTimeOverlapEndTime (index) {
            let isOverlap = false
            if (!this.form.workdayData[index].isWorkday) {
                if (this.form.workdayData[index].checkinTime > this.form.workdayData[index].endTime) {
                    isOverlap = true
                }
            }

            this.errors.endTime[index] = isOverlap ? `${this.$vuetify.lang.t('$vuetify.workdayForm.checkInTimeOverlapEndTime')} ${this.form.workdayData[index].endTime}.` : null
            this.errors.checkInTime[index] = isOverlap ? `${this.$vuetify.lang.t('$vuetify.workdayForm.endTimeOverlapCheckInTime')}  ${this.form.workdayData[index].checkinTime}.` : null

            return isOverlap
        },

        isStartTimeOverlapLateCheckInTime (index) {
            let isOverlap = false
            if (this.form.workdayData[index].isWorkday) {
                if (this.form.workdayData[index].startTime > this.form.workdayData[index].lateCheckinTime) {
                    isOverlap = true
                }
            }

            this.errors.startTime[index] = isOverlap ? `${this.$vuetify.lang.t('$vuetify.workdayForm.startTimeOverlapLateCheckInTime')} ${this.form.workdayData[index].startTime}.` : null
            this.errors.lateCheckInTime[index] = isOverlap ? `${this.$vuetify.lang.t('$vuetify.workdayForm.lateCheckInTimeOverlapStartTime')} ${this.form.workdayData[index].lateCheckinTime}.` : null

            return isOverlap
        },

        isLateCheckInTimeOverlapLastCheckInTime (index) {
            let isOverlap = false
            if (this.form.workdayData[index].isWorkday) {
                if (this.form.workdayData[index].lateCheckinTime > this.form.workdayData[index].lastCheckinTime) {
                    isOverlap = true
                }
            }

            this.errors.lateCheckInTime[index] = isOverlap ? `${this.$vuetify.lang.t('$vuetify.workdayForm.lateCheckInTimeOverlapLastCheckInTime')} ${this.form.workdayData[index].lateCheckinTime}.` : null
            this.errors.lastCheckInTime[index] = isOverlap ? `${this.$vuetify.lang.t('$vuetify.workdayForm.lastCheckInTimeOverlapLateCheckInTime')} ${this.form.workdayData[index].lastCheckinTime}.` : null

            return isOverlap
        },

        isLastCheckInTimeOverlapEndTime (index) {
            let isOverlap = false
            if (this.form.workdayData[index].isWorkday) {
                if (this.form.workdayData[index].lastCheckinTime > this.form.workdayData[index].endTime) {
                    isOverlap = true
                }
            }

            this.errors.lastCheckInTime[index] = isOverlap ? `${this.$vuetify.lang.t('$vuetify.workdayForm.lastCheckInTimeOverlapEndTime')} ${this.form.workdayData[index].lastCheckinTime}.` : null
            this.errors.endTime[index] = isOverlap ? `${this.$vuetify.lang.t('$vuetify.workdayForm.endTimeOverlapLastCheckInTime')} ${this.form.workdayData[index].endTime}.` : null

            return isOverlap
        },

        getNextDayIndex (index) {
            const indexNextDay = index + 1
            return (indexNextDay > 6) ? 0 : indexNextDay
        },

        getPreviousDayIndex (index) {
            const indexPrevDay = index - 1
            return (indexPrevDay < 0) ? 6 : indexPrevDay
        },

        convertStrToMinute (data) {
            let result = 0

            const [hourStr, minuteStr, secondStr] = data.split(':')
            if (data.split(':').length < 2) {
                return 0
            }
            const hourInMinute = parseInt(hourStr) * 60
            const minute = parseInt(minuteStr)
            let secondInMinute = (parseInt(secondStr / 1000))

            if (!secondInMinute) {
                secondInMinute = 0
            }

            result = hourInMinute + minute + secondInMinute

            return result
        },

        isEndTimeOverlapNextDayCheckIn (index) {
            const nextDayIndex = this.getNextDayIndex(index)

            // when next day is current day, cannot check
            if (this.todayIndex === nextDayIndex) {
                return false
            }

            if (
                !this.form.workdayData[nextDayIndex].isWorkday ||
                    !this.form.workdayData[index].isWorkday
            ) {
                return false
            }

            const startTimeStr = this.form.workdayData[index].startTime
            const endTimeStr = this.form.workdayData[index].endTime
            const checkInNextDayTimeStr = this.form.workdayData[nextDayIndex].checkinTime

            const isOverlap = this.checkTimeOverlap(startTimeStr, endTimeStr, checkInNextDayTimeStr)
            this.errors.endTime[index] = isOverlap ? 'เวลา end time ต้องน้อยกว่า start check in ของวันถัดไป' : null
            this.errors.checkInTime[nextDayIndex] = isOverlap ? this.$vuetify.lang.t('$vuetify.workdayForm.checkInShouldAfterEndTime') : null

            return isOverlap
        },

        isCheckInTimeOverlapPreviousDayEnd (index) {
            const prevDayIndex = this.getPreviousDayIndex(index)

            // when index and prevDay is current day , cannot check
            if (this.todayIndex === index || this.todayIndex === prevDayIndex) {
                return false
            }

            if (
                !this.form.workdayData[prevDayIndex].isWorkday ||
                    !this.form.workdayData[index].isWorkday
            ) {
                return false
            }

            const startTimeStr = this.form.workdayData[prevDayIndex].startTime
            const endTimeStr = this.form.workdayData[prevDayIndex].endTime
            const checkInNextDayTimeStr = this.form.workdayData[index].checkinTime

            const isOverlap = this.checkTimeOverlap(startTimeStr, endTimeStr, checkInNextDayTimeStr)
            this.errors.checkInTime[index] = isOverlap ? this.$vuetify.lang.t('$vuetify.workdayForm.checkInShouldAfterEndTime') : null
            this.errors.endTime[prevDayIndex] = isOverlap ? 'เวลา end time ต้องน้อยกว่า start check in ของวันถัดไป' : null

            return isOverlap
        },

        checkTimeOverlap (startTimeStr, endTimeStr, checkInNextDayTimeStr) {
            const startTime = this.convertStrToMinute(startTimeStr)
            let endTime = this.convertStrToMinute(endTimeStr)
            const nextTime = this.convertStrToMinute(checkInNextDayTimeStr)

            if (endTime > startTime) {
                // 1440 is 24 hours
                const minutesInOneDay = 60 * 24
                endTime = endTime - minutesInOneDay
            }

            if (endTime < nextTime) {
                return false
            }

            return true
        },

        checkError (error) {
            // 7 is 7 day for check error
            for (let i = 0; i < 7; i++) {
                if (error.checkInTime[i] || error.startTime[i] || error.lateCheckInTime[i] || error.lastCheckInTime[i] ||
                    error.breakhours[i] || error.endTime[i] || error.totalWorkTime[i]) {
                    return true
                }
            }

            return false
        },

        async save () {
            if (!this.$refs.form.validate()) {
                return
            }

            const isErrors = this.checkError(this.errors)

            if (isErrors) {
                return
            }

            this.addMissingSecondsInInputTime()
            await this.update()
        },

        addMissingSecondsInInputTime () {
            const { workdayData } = this.form

            for (let i = 0; i < workdayData.length; i++) {
                const { checkinTime, startTime, endTime } = workdayData[i]
                // after split by ':' if length of values is not 3 that mean time is missing second
                if (checkinTime.split(':').length !== 3) {
                    this.form.workdayData[i].checkinTime = `${checkinTime}:00`
                }
                if (startTime.split(':').length !== 3) {
                    this.form.workdayData[i].startTime = `${startTime}:00`
                }
                if (endTime.split(':').length !== 3) {
                    this.form.workdayData[i].endTime = `${endTime}:00`
                }
            }
        },

        async update () {
            this.$store.dispatch('setLoading', true)
            const id = this.$route.params.id

            const res = await this.axios.put(`workday/edit/${id}`, this.form)
            this.$store.dispatch('setLoading', false)
            if (res && res.data && res.data.status === 'ok') {
                this.successMsgNotify()
                this.$router.push({ name: 'TimeTableSetting' }, () => {})
            } else if (res && res.data && res.data.status === 'error_inputs') {
                this.errors = res.data.errors
                this.errorMsgNotify()
            } else {
                this.errorMsgNotify()
            }
        },

        cancel () {
            this.$router.push({ name: 'TimeTableSetting' })
        },

        successMsgNotify () {
            this.$notify({
                title: this.$vuetify.lang.t('$vuetify.workdayForm.congratulation'),
                text: this.$vuetify.lang.t('$vuetify.workdayForm.msgUpdateSuccess'),
                type: 'success'
            })
        },

        errorMsgNotify () {
            this.$notify({
                title: this.$vuetify.lang.t('$vuetify.workdayForm.msgError'),
                text: this.$vuetify.lang.t('$vuetify.workdayForm.incorrectWorkTimePeriod'),
                type: 'error'
            })
        },

        async getWorkday (id) {
            const res = await this.axios.get(`workday/${id}`)
            if (res && res.data && res.data.status === 'ok') {
                const data = res.data.data
                this.form.workdayData = data.workday
                this.bureau = data.bureau
                this.todayIndex = data.todayIndex

                this.showWorkday = true
                this.canSaveOrUpdate = true
            }
        },

        async checkParams () {
            const id = this.$route.params.id
            if (id) {
                await this.getWorkday(id)
            }
        },

        convertStrToDateTime (data) {
            const date = new Date()
            const [hoursStr, minuteStr, secondStr] = data.split(':')

            date.setHours(parseInt(hoursStr))
            date.setMinutes(parseInt(minuteStr))
            if (secondStr) {
                date.setSeconds(parseInt(secondStr))
            } else {
                date.setSeconds(0)
            }

            return date
        },

        convertTimeToStr (hours, minutes) {
            if (hours < 0 || !hours) {
                hours = 0
            }
            if (minutes < 0 || !minutes) {
                minutes = 0
            }

            return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
        },

        calTotalWorkHours (index) {
            const startTimeStr = this.form.workdayData[index].startTime
            const endTimeStr = this.form.workdayData[index].endTime
            const [hoursBreakStr, minuteBreakStr] = this.form.workdayData[index].breakHours.split(':')

            this.errors.breakhours[index] = (hoursBreakStr === '') ? this.$vuetify.lang.t('$vuetify.workdayForm.breakhoursIsRequired') : null

            const dateTimeStart = this.convertStrToDateTime(startTimeStr)
            const dateTimeEnd = this.convertStrToDateTime(endTimeStr)

            // hrs to ms
            const hoursBreak = parseInt(hoursBreakStr) * (1000 * 60 * 60)
            // m to ms
            const minutesBreak = parseInt(minuteBreakStr) * (1000 * 60)

            if (dateTimeEnd.getHours() <= dateTimeStart.getHours()) {
                const nextDay = dateTimeEnd.getDate() + 1
                dateTimeEnd.setDate(nextDay)
            }

            // date time end and date time start is ms
            const totalMilliseconds = dateTimeEnd.getTime() - dateTimeStart.getTime() - (hoursBreak + minutesBreak)

            // ms to hrs
            const hours = Math.floor(((totalMilliseconds) / (1000 * 60 * 60)) % 24)
            // ms to m
            const minutes = Math.floor(((totalMilliseconds) / (1000 * 60)) % 60)

            const timeStr = this.convertTimeToStr(hours, minutes)
            this.form.workdayData[index].totalWorkHours = timeStr

            this.errors.totalWorkTime[index] = (timeStr === '00:00:00') ? this.$vuetify.lang.t('$vuetify.workdayForm.incorrectWorkTimePeriod') : null
        }
    },

    async mounted () {
        await this.checkParams()
    },
    watch: {
        'form.workdayData': {
            handler: function (v) {
                for (let i = 0; i < v.length; i++) {
                    this.calTotalWorkHours(i)
                    if (this.isCheckInTimeOverlapStartTime(i)) {
                        break
                    } else if (this.isStartTimeOverlapLateCheckInTime(i)) {
                        break
                    } else if (this.isLateCheckInTimeOverlapLastCheckInTime(i)) {
                        break
                    } else if (this.isLastCheckInTimeOverlapEndTime(i)) {
                        break
                    } else if (this.isCheckInTimeOverlapEndTime(i)) {
                        break
                    } else if (this.isEndTimeOverlapNextDayCheckIn(i)) {
                        break
                    } else if (this.isCheckInTimeOverlapPreviousDayEnd(i)) {
                        break
                    }
                }
            },
            deep: true
        }
    }

}
</script>

<style lang="scss" scoped>
    .header {
        display: flex;
        justify-content: space-between;
        margin-bottom: 2rem;
    }
    .card-container-header {
        padding: 2rem 3rem 0.5rem;
        margin-bottom: 0.05rem;
        box-shadow: 0 5px 25px 0 rgba(0, 0, 0, 0.05);
        border-radius: 15px 15px 0px 0px !important;
        border-bottom: 1px solid rgba(0, 0, 0, 0.25);
    }
    .card-container-content {
        padding: 0 3rem 2rem;
        margin-bottom: 0.05rem;
        box-shadow: 0 5px 25px 0 rgba(0, 0, 0, 0.05);
        border-radius: 0px 0px 15px 15px !important;
    }
    .column-head {
        display: flex;
        flex-wrap: wrap;
        align-items: center;

        & > div {
            width: 100%;
            display: flex;
            align-items: center;
        }

        .column-item {
            width: 14%;
            padding-right: 14px;
            box-sizing: border-box;
        }
        .column-item:last-child {
            width: 22%;
            text-align: center;
            padding-right: 1px;
        }
        .column-item:first-child {
            width: 10%;
        }

        .row-item {
            width: 100%;
            padding-left: 15%;
            display: flex;
            font-size: 14px;
            color: #6c98ba;
            margin-top: -15px;

            span {
                margin-left: -95px;
            }
        }
    }
    .name-input {
        width: 200px;
    }

    .icon-text {
        font-size: 12px;
        margin: 7px 0px 8px 6px;
    }

    .label-text {
        font-size: 18px;
    }

    .text-box {
        box-shadow: 0 0 1px 0;
    }

    ::v-deep .fas {
        font-family: "Font Awesome 5 Pro";
        font-weight: 900;
        padding-left: 10px;
        color: #d76436;
    }

    @media only screen and (max-width: 1715px) {
        .card-container-header {
            font-size: 13px;
        }
        .card-container-content {
            font-size: 13px;
        }
        ::v-deep .v-input input {
            max-height: 30px;
            font-size: 13px;
        }
        .icon-text {
            font-size: 10px;
        }
        .column-head {
            zoom: 80%;
            .column-item:last-child {
                width: 30%;
            }
        }
    }
    @media only screen and (max-width: 1415px) {
        .card-container-header {
            font-size: 11px;
        }
        .card-container-content {
            font-size: 11px;
        }
        ::v-deep .v-input input {
            max-height: 30px;
            font-size: 11px;
        }
        .icon-text {
            font-size: 8px;
        }
        .column-head {
            zoom: 70%;
            .column-item:last-child {
                width: 25%;
            }
        }
    }
    ::v-deep .v-text-field input[readonly="readonly"] {
        background-color: #f0f0f0;
        color: gray;
    }
    ::v-deep .breadcrumb-container .v-breadcrumbs {
        padding-bottom: 18px;
    }
</style>
